import {
  RawDineInSession,
  DineInSession,
  DineInSessionMember,
  DineInRound,
  DineInRoundItem,
} from "../../../interfaces/DineInObject";
import { lineIdentityKey, normalizeDineInItemStatus } from "../../helpers/DineInSummaryHelper";

export interface ConvertedDineInSession {
  session: DineInSession;
  members: DineInSessionMember[];
  rounds: DineInRound[];
  roundItems: DineInRoundItem[];
}

/**
 * Flattens the nested dining session response — every dine-in route echoes
 * the same envelope, rounds carrying their own items — into the separate
 * rounds[]/roundItems[] arrays the slices store, joined back together by
 * roundId in dineInFeedBuilder.ts and the kitchen board's builder.
 *
 * Items are carried through ONE-FOR-ONE, exactly as sent. An earlier version
 * merged entries sharing (roundId, productId, addedBy) and summed their
 * quantities, because the backend can send several entries for the same pair —
 * one per distinct "add" action, not a running total. That merge is no longer
 * safe: items now carry their own kitchen status (ORDERED → ACCEPTED /
 * REJECTED → SERVED), and collapsing a served dosa into an unserved one would
 * erase precisely what the kitchen board is for. Merging for readability still
 * happens for the diner, but at display time — see groupFeedItemsForDisplay.
 */
export function convertRawDineInSession(raw: RawDineInSession): ConvertedDineInSession {
  const session: DineInSession = {
    sessionId: raw.sessionId ?? "",
    businessId: raw.businessId ?? "",
    // Comes back as a string ("12") from the real API despite being numeric.
    tableNumber: Number(raw.tableNumber ?? 0),
    status: raw.status ?? "",
    creatorId: raw.creatorId ?? "",
    sessionCode: raw.sessionCode ?? "",
    createdAt: raw.createdAt ?? "",
  };

  const members: DineInSessionMember[] = (raw.members ?? []).map((m) => ({
    sessionId: m.sessionId ?? session.sessionId,
    memberId: m.memberId ?? "",
    memberName: m.memberName ?? "Someone",
  }));

  const rounds: DineInRound[] = (raw.rounds ?? []).map((r) => ({
    sessionId: session.sessionId,
    roundId: r.roundId ?? "",
    roundNumber: r.roundNumber ?? 0,
    status: r.status ?? "",
    orderedAt: r.orderedAt ?? "",
  }));

  // Occurrence counter for the itemKey fallback. Two raw entries identical in
  // round, product, person AND status are genuinely indistinguishable without
  // a server id, so they're told apart by their position among their own kind.
  // Scoped per (round, product, person, status) rather than per round so that
  // an unrelated item appearing between them can't shift either one's key.
  const occurrences = new Map<string, number>();
  const roundItems: DineInRoundItem[] = [];

  for (const r of raw.rounds ?? []) {
    const roundId = r.roundId ?? "";
    for (const item of r.items ?? []) {
      const productId = item.productId ?? "";
      const addedBy = item.addedBy ?? "";
      const status = normalizeDineInItemStatus(item.status);

      const variantId = item.variantId ?? undefined;

      const groupKey = lineIdentityKey({ productId, variantId }, roundId, addedBy, status);
      const occurrence = occurrences.get(groupKey) ?? 0;
      occurrences.set(groupKey, occurrence + 1);

      roundItems.push({
        roundId,
        // The response calls this `itemId`; every route that acts on an item
        // calls it `roundItemId` (/dine-in-items/{roundItemId}). Normalised to
        // the path spelling here, since that's what it's used for.
        roundItemId: item.itemId,
        // The composite is now a fallback only — it exists for the window
        // where a response predates the id, and because a client-side key
        // can't address a row on the server, anything that MUTATES an item
        // must read roundItemId directly rather than trusting this.
        itemKey: item.itemId ?? `${groupKey}__${occurrence}`,
        productId,
        variantId,
        quantity: item.quantity && item.quantity > 0 ? item.quantity : 1,
        addedBy,
        addedAt: item.addedAt ?? "",
        status,
        statusNote: item.statusNote ?? undefined,
      });
    }
  }

  return { session, members, rounds, roundItems };
}

/**
 * GET /api/v1/dining/businesses/{businessId}/dine-in-sessions returns an array
 * of the exact same envelope, one per live table, so this is a plain map
 * rather than a second converter — there is no list-specific shape to
 * translate.
 *
 * `contents` is a real collection here, NOT the array-wrapped single object
 * the legacy envelope uses elsewhere, which is why the service unwraps it with
 * coerceList rather than coerceSingle.
 */
export function convertRawDineInSessionList(
  raw: RawDineInSession[]
): ConvertedDineInSession[] {
  return raw.map(convertRawDineInSession);
}
